import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlarmClock, AlarmClockOff, Archive, CheckCircle2, CircleCheckBig, ImageIcon, MoreHorizontal, Pencil, Plus, RotateCcw, SquarePen, Undo2, Users } from "lucide-react";
import { useAccount, useSharing } from "@/data/cloud";
import { ShareDialog } from "./ShareDialog";
import type { Area, Project, Space } from "@desk/contracts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip } from "@/components/ui/misc";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApi } from "@/data/context";
import { describeError } from "@/data/api";
import { writeContainers, type ContainerChange } from "@/data/containers";
import { useHierarchy, useTaskList } from "@/data/hooks";
import { CustomSnoozeDialog, SnoozePresetItems } from "@/features/snooze/SnoozeControls";
import { IconDialog } from "./IconDialog";
import { DetailsDialog } from "./DetailsDialog";
import { formatInstant, wakeToRestore } from "@/lib/dates";
import { plural } from "@/lib/utils";
import { navigate } from "@/router";

export type Target = { kind: "area"; area: Area } | { kind: "space"; space: Space } | { kind: "project"; project: Project };

/** Name limits per kind, matching the API contracts. */
export const NAME_MAX = { area: 120, space: 120, project: 160 } as const;

export function nameOf(t: Target) {
  return t.kind === "area" ? t.area.name : t.kind === "space" ? t.space.name : t.project.name;
}
export function idOf(t: Target) {
  return t.kind === "area" ? t.area.id : t.kind === "space" ? t.space.id : t.project.id;
}

/**
 * Container writes. `changes` are shown optimistically (sidebar, headers, pickers) until the server
 * confirms; `undoChanges` do the same for the toast's Undo.
 */
export function useContainerOps() {
  const api = useApi();
  const qc = useQueryClient();
  const run = async (
    what: string,
    fn: () => Promise<unknown>,
    success?: string,
    undo?: () => Promise<unknown>,
    changes?: { apply?: ContainerChange[]; undo?: ContainerChange[] },
  ) => {
    try {
      await writeContainers(qc, fn, changes?.apply);
      if (success)
        toast(
          success,
          undo
            ? {
                action: {
                  label: "Undo",
                  onClick: () =>
                    void writeContainers(qc, undo, changes?.undo).catch((e) => toast.error("Couldn't undo", { description: describeError(e) })),
                },
              }
            : undefined,
        );
      return true;
    } catch (e) {
      toast.error(`Couldn't ${what}`, { description: describeError(e) });
      return false;
    }
  };
  /** Rename any container; shared by the rename dialog and inline sidebar rename. */
  const rename = (target: Target, name: string) => {
    const id = idOf(target);
    return run(
      "rename",
      () =>
        target.kind === "area"
          ? api.updateArea(id, { name })
          : target.kind === "space"
            ? api.updateSpace(id, { name })
            : api.updateProject(id, { name }),
      undefined,
      undefined,
      { apply: [{ kind: target.kind, id, patch: { name } }] },
    );
  };
  return { api, run, rename, qc };
}

const nowIso = () => new Date().toISOString();

/** Rename dialog for areas/spaces/projects. */
function RenameDialog({ target, open, onOpenChange }: { target: Target; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { rename } = useContainerOps();
  const [name, setName] = useState(nameOf(target));
  const save = async () => {
    const v = name.trim();
    if (!v || v === nameOf(target)) return onOpenChange(false);
    // On failure the dialog stays open with the entered name.
    if (await rename(target, v)) onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[380px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <DialogBody>
            <DialogTitle>Rename</DialogTitle>
            <Input aria-label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} maxLength={NAME_MAX[target.kind]} />
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default">
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Archive with an explanation of the consequences and the restore path. */
function ArchiveDialog({ target, open, onOpenChange }: { target: Target; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { api, run } = useContainerOps();
  const h = useHierarchy();
  const q =
    target.kind === "area" ? { areaId: target.area.id } : target.kind === "space" ? { spaceId: target.space.id } : { projectId: target.project.id };
  const { tasks } = useTaskList({ ...q, includeSnoozed: true, limit: 500 }, undefined, open);
  const openCount = tasks.filter((t) => t.status !== "done" && t.status !== "cancelled").length;
  const children =
    target.kind === "area"
      ? h.spacesOf(target.area.id).length
      : target.kind === "space"
        ? h.projectsOf(target.space.id).length
        : 0;
  const childLabel = target.kind === "area" ? plural(children, "space") : plural(children, "project");
  const archive = async () => {
    const id = idOf(target);
    const ok = await run(
      "archive",
      () => api.containerAction(target.kind, id, "archive"),
      `Archived ${nameOf(target)}`,
      () => api.containerAction(target.kind, id, "restore"),
      { apply: [{ kind: target.kind, id, patch: { archivedAt: nowIso() } }], undo: [{ kind: target.kind, id, patch: { archivedAt: null } }] },
    );
    if (ok) {
      onOpenChange(false);
      navigate({ name: "today" });
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px]">
        <DialogBody>
          <DialogTitle>Archive {nameOf(target)}?</DialogTitle>
          <DialogDescription>
            {target.kind !== "project" && children > 0 ? `It and its ${childLabel} leave` : "It leaves"} the sidebar and destination pickers.{" "}
            {openCount > 0
              ? `Its ${plural(openCount, "open task")} aren't deleted or completed, and stay findable in search.`
              : "Nothing is deleted."}{" "}
            Restore it any time from Settings → Archived.
          </DialogDescription>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="default" onClick={() => void archive()}>
            <Archive />
            Archive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function completionChanges(project: Project) {
  const at = nowIso();
  return {
    apply: [{ kind: "project" as const, id: project.id, patch: { state: "completed", completedAt: at, settledAt: project.settledAt ?? at } }],
    undo: [{ kind: "project" as const, id: project.id, patch: { state: "active", completedAt: null, settledAt: null, snoozedUntil: null } }],
  };
}

/** Completing a project with open tasks requires an explicit choice (PRD §3, G14). */
export function CompleteProjectDialog({ project, open, onOpenChange }: { project: Project; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { api, run } = useContainerOps();
  const { tasks } = useTaskList({ projectId: project.id, includeSnoozed: true, limit: 500 }, undefined, open);
  const openCount = tasks.filter((t) => t.status !== "done" && t.status !== "cancelled").length;
  const complete = async (openTasks: "complete" | "move_to_space" | "keep") => {
    const ok = await run(
      "complete the project",
      () => api.completeProject(project.id, openTasks),
      `Completed ${project.name}`,
      () => api.containerAction("project", project.id, "reopen"),
      completionChanges(project),
    );
    if (ok) onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={openCount > 0 ? "max-w-[560px]" : "max-w-[440px]"}>
        <DialogBody>
          <DialogTitle>Complete {project.name}?</DialogTitle>
          {openCount > 0 ? (
            <DialogDescription>{plural(openCount, "task is", "tasks are")} still open. Choose what happens to them.</DialogDescription>
          ) : (
            <DialogDescription>All of its tasks are finished.</DialogDescription>
          )}
        </DialogBody>
        <DialogFooter className="flex-wrap">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {openCount > 0 ? (
            <>
              <Button variant="outline" onClick={() => void complete("keep")}>
                Keep them open here
              </Button>
              <Button variant="outline" onClick={() => void complete("move_to_space")}>
                Move to space
              </Button>
              <Button variant="default" onClick={() => void complete("complete")}>
                Complete them too
              </Button>
            </>
          ) : (
            <Button variant="default" onClick={() => void complete("keep")}>
              Complete project
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Inline "new space/project" creation dialog. */
export function NewContainerDialog({
  kind,
  parentId,
  open,
  onOpenChange,
}: {
  kind: "area" | "space" | "project";
  parentId?: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { api, run } = useContainerOps();
  const [name, setName] = useState("");
  // One id per draft: retries of the same draft reuse it (idempotent), a new draft gets a new one.
  const [clientId, setClientId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const create = async () => {
    const v = name.trim();
    if (!v || busy) return;
    setBusy(true);
    let created: { id: string } | undefined;
    const ok = await run(`create the ${kind}`, async () => {
      created =
        kind === "area" ? await api.createArea(v, clientId) : kind === "space" ? await api.createSpace(parentId!, v, clientId) : await api.createProject(parentId!, v, clientId);
    });
    setBusy(false);
    if (ok) {
      setName("");
      setClientId(crypto.randomUUID());
      onOpenChange(false);
      if (created?.id) navigate({ name: kind, id: created.id });
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[380px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <DialogBody>
            <DialogTitle>New {kind}</DialogTitle>
            <Input aria-label={`${kind} name`} placeholder="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} maxLength={NAME_MAX[kind]} />
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={!name.trim() || busy}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hover actions for spaces/projects (and the "…" menu for areas). Settling means checking it off
 * (G19): an active project shows Complete + Snooze, an active space shows Settle + Snooze, and a
 * resting one (settled, snoozed, or a completed project) shows Unsettle, which is Reopen for a
 * completed project. `openCount`, when known, lets Complete skip the open-tasks dialog when there is
 * nothing to decide.
 */
export function ContainerActions({
  target,
  compact = true,
  trigger,
  openCount,
  menuOpen,
  onMenuOpenChange,
  menuOnly,
}: {
  target: Target;
  compact?: boolean;
  trigger?: ReactNode;
  openCount?: number;
  /** Controlled menu (the sidebar's right-click menu). */
  menuOpen?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
  /** Only the menu, without the hover quick actions. */
  menuOnly?: boolean;
}) {
  const { api, run } = useContainerOps();
  const h = useHierarchy();
  const [dialog, setDialog] = useState<null | "rename" | "icon" | "details" | "archive" | "snooze" | "new" | "complete" | "share">(null);
  const account = useAccount();
  const sharing = useSharing();
  const name = nameOf(target);
  const id = idOf(target);
  const attention = target.kind === "space" ? target.space : target.kind === "project" ? target.project : null;
  const settled = !!attention?.settledAt;
  const snoozed = !!attention?.snoozedUntil && attention.snoozedUntil > new Date().toISOString();
  const completed = target.kind === "project" && target.project.state === "completed";
  const resting = settled || snoozed || completed;
  const kind = target.kind;

  const change = (patch: Record<string, unknown>): ContainerChange[] => [{ kind, id, patch }];
  const settleChange = change({ settledAt: nowIso() });
  const unsettleChange = change({ settledAt: null, snoozedUntil: null });
  const settle = () =>
    kind !== "area" &&
    void run(
      settled ? "unsettle" : "settle",
      () => api.containerAction(kind, id, settled ? "unsettle" : "settle"),
      settled ? `${name} is back in the sidebar` : `Settled ${name}`,
      () => api.containerAction(kind, id, settled ? "settle" : "unsettle"),
      settled ? { apply: unsettleChange, undo: settleChange } : { apply: settleChange, undo: unsettleChange },
    );
  /** Back to the active sidebar: Reopen for a completed project, otherwise clear settle and snooze. */
  const unsettle = () =>
    kind !== "area" &&
    void (completed
      ? run("reopen", () => api.containerAction("project", id, "reopen"), `Reopened ${name}`, undefined, {
          apply: change({ state: "active", completedAt: null, settledAt: null, snoozedUntil: null }),
        })
      : run("unsettle", () => api.containerAction(kind, id, "unsettle"), `${name} is back in the sidebar`, undefined, { apply: unsettleChange }));
  const completeProject = () => {
    if (target.kind !== "project") return;
    if (openCount !== 0) return setDialog("complete");
    void run(
      "complete the project",
      () => api.completeProject(id, "keep"),
      `Completed ${name}`,
      () => api.containerAction("project", id, "reopen"),
      completionChanges(target.project),
    );
  };
  const snooze = (iso: string) => {
    if (kind === "area") return;
    // Undo restores the wake time that was set before, or wakes it if that time has already passed.
    const previous = attention?.snoozedUntil ?? null;
    const restore = () => wakeToRestore(previous);
    void run(
      "snooze",
      () => api.snoozeContainer(kind, id, iso),
      `${name} returns ${formatInstant(iso, h.timezone)}`,
      () => {
        const at = restore();
        return at ? api.snoozeContainer(kind, id, at) : api.containerAction(kind, id, "unsnooze");
      },
      {
        apply: change({ snoozedUntil: iso }),
        // Read when Undo is clicked, so an expired wake time isn't restored.
        get undo() {
          return change({ snoozedUntil: restore() });
        },
      },
    );
  };

  return (
    <div className={menuOnly ? "contents" : "flex items-center"} onClick={(e) => e.stopPropagation()}>
      {kind !== "area" && compact && !menuOnly && (
        <>
          {resting ? (
            <Tooltip content={completed ? "Reopen" : "Unsettle"}>
              <Button size="icon-sm" variant="quiet" aria-label={completed ? `Reopen ${name}` : `Unsettle ${name}`} onClick={unsettle}>
                {completed ? <RotateCcw /> : <Undo2 />}
              </Button>
            </Tooltip>
          ) : kind === "project" ? (
            <Tooltip content="Complete project">
              <Button size="icon-sm" variant="quiet" aria-label={`Complete ${name}`} onClick={completeProject}>
                <CircleCheckBig />
              </Button>
            </Tooltip>
          ) : (
            <Tooltip content="Settle">
              <Button size="icon-sm" variant="quiet" aria-label={`Settle ${name}`} onClick={settle}>
                <CheckCircle2 />
              </Button>
            </Tooltip>
          )}
          {!resting && (
            <DropdownMenu modal={false}>
              <Tooltip content="Snooze">
                <DropdownMenuTrigger asChild>
                  <Button size="icon-sm" variant="quiet" aria-label={`Snooze ${name}`}>
                    <AlarmClock />
                  </Button>
                </DropdownMenuTrigger>
              </Tooltip>
              <DropdownMenuContent align="start" side="right">
                <SnoozePresetItems onPick={snooze} onCustom={() => setDialog("snooze")} />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
      )}
      <DropdownMenu modal={false} open={menuOpen} onOpenChange={onMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          {trigger ?? (
            <Button size="icon-sm" variant="quiet" aria-label={`More actions for ${name}`}>
              <MoreHorizontal />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side={compact ? "right" : "bottom"}>
          {kind === "area" && (
            <DropdownMenuItem onSelect={() => setDialog("new")}>
              <Plus />
              New space
            </DropdownMenuItem>
          )}
          {kind === "area" &&
            account.signedIn &&
            account.status?.state !== "needs_choice" &&
            !sharing.data?.areas.some((a) => a.areaId === id && a.role === "member") && (
              <DropdownMenuItem onSelect={() => setDialog("share")}>
                <Users />
                Share…
              </DropdownMenuItem>
            )}
          {kind === "space" && (
            <DropdownMenuItem onSelect={() => setDialog("new")}>
              <Plus />
              New project
            </DropdownMenuItem>
          )}
          {kind === "space" && !compact && (
            <DropdownMenuItem onSelect={settle}>
              {settled ? <Undo2 /> : <CheckCircle2 />}
              {settled ? "Unsettle" : "Settle"}
            </DropdownMenuItem>
          )}
          {kind === "project" && !compact && settled && !completed && (
            <DropdownMenuItem onSelect={unsettle}>
              <Undo2 />
              Unsettle
            </DropdownMenuItem>
          )}
          {kind !== "area" && !compact && !completed && (
            snoozed ? (
              <DropdownMenuItem
                onSelect={() => void run("unsnooze", () => api.containerAction(kind, id, "unsnooze"), undefined, undefined, { apply: change({ snoozedUntil: null }) })}
              >
                <AlarmClockOff />
                Unsnooze
              </DropdownMenuItem>
            ) : (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <AlarmClock />
                  Snooze
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <SnoozePresetItems onPick={snooze} onCustom={() => setDialog("snooze")} />
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )
          )}
          {target.kind === "project" &&
            (completed ? (
              <DropdownMenuItem onSelect={unsettle}>
                <RotateCcw />
                Reopen project
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={completeProject}>
                <CircleCheckBig />
                {openCount === 0 ? "Complete project" : "Complete project…"}
              </DropdownMenuItem>
            ))}
          <DropdownMenuItem onSelect={() => setDialog("rename")}>
            <Pencil />
            Rename
          </DropdownMenuItem>
          {target.kind !== "project" && (
            <DropdownMenuItem onSelect={() => setDialog("icon")}>
              <ImageIcon />
              Change icon…
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setDialog("details")}>
            <SquarePen />
            Edit details…
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setDialog("archive")}>
            <Archive />
            Archive…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialog === "rename" && <RenameDialog target={target} open onOpenChange={(o) => !o && setDialog(null)} />}
      {dialog === "icon" && target.kind !== "project" && <IconDialog target={target} open onOpenChange={(o) => !o && setDialog(null)} />}
      {dialog === "details" && <DetailsDialog target={target} open onOpenChange={(o) => !o && setDialog(null)} />}
      {dialog === "share" && <ShareDialog areaId={id} areaName={name} open onOpenChange={(o) => !o && setDialog(null)} />}
      {dialog === "archive" && <ArchiveDialog target={target} open onOpenChange={(o) => !o && setDialog(null)} />}
      {dialog === "snooze" && (
        <CustomSnoozeDialog open title={`Snooze ${name} until`} onOpenChange={(o) => !o && setDialog(null)} onConfirm={(iso) => snooze(iso)} />
      )}
      {dialog === "new" && (
        <NewContainerDialog kind={kind === "area" ? "space" : "project"} parentId={id} open onOpenChange={(o) => !o && setDialog(null)} />
      )}
      {dialog === "complete" && target.kind === "project" && (
        <CompleteProjectDialog project={target.project} open onOpenChange={(o) => !o && setDialog(null)} />
      )}
    </div>
  );
}

/**
 * Right-click on a sidebar row opens the same actions as its "…" menu, at the pointer. It stays
 * mounted after the first use so a dialog opened from the menu outlives the menu.
 */
export function ContainerContextMenu({ target, openCount, children }: { target: Target; openCount?: number; children: ReactNode }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const [open, setOpen] = useState(false);
  return (
    <div
      className="contents"
      onContextMenu={(e) => {
        e.preventDefault();
        setAt({ x: e.clientX, y: e.clientY });
        setOpen(true);
      }}
    >
      {children}
      {at && (
        <ContainerActions
          target={target}
          compact={false}
          menuOnly
          openCount={openCount}
          menuOpen={open}
          onMenuOpenChange={setOpen}
          trigger={<span aria-hidden className="pointer-events-none fixed size-px" style={{ left: at.x, top: at.y }} />}
        />
      )}
    </div>
  );
}
